/**
 * InfinitSales - Janela de conversa COMPARTILHADA (shell do inbox, 2026-09-23)
 *
 * Extraida do ChatWindow do Messenger. Tudo que e do CANAL vem do adaptador:
 * servico HTTP, chave opaca, janela de 24 h (canais da Meta) ou sempre
 * aberta (widget), tipos de midia que o canal aceita (documento, audio),
 * gravacao de voz, textos e o que so um canal renderiza (botoes interativos
 * e contexto de pagina no widget, @handle no Instagram). O resto e o MESMO
 * produto dos inboxes de WhatsApp: cabecalho, drawer do contato, etiquetas,
 * follow-up, comentario, notificacao, AI Workspace, encaminhar, handoff.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Send, User, Loader2, Clock, ImageOff,
  MoreVertical, UserCheck, MessageSquarePlus, Bell, Zap, Tags,
  History, Settings, Bot, Forward, Mic, FileText,
} from 'lucide-react';
import RecordingBar from '../RecordingBar';
import AttachButton from '../chat/AttachButton';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import fileUploadService from '../../services/global/fileUploadService';
import { useTheme } from '../../contexts/ThemeContext';
import ChatDateDivider, { groupTimelineByDay } from '../chat/ChatDateDivider';
import MessageStatus from '../chat/MessageStatus';
import { useAuth } from '../../contexts/AuthContext';
import renderVariablesInText from '../../utils/messageVariables';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import useFeatureFlags, { getFeatureFlag } from '../../hooks/useFeatureFlags';
import { getChatEntry, setChatEntry, setChatMessages } from '../../services/inbox/chatCache';
import { useNotifications } from '../../contexts/NotificationsContext';
import assignmentService from '../../services/global/assignmentService';
import aiAgentService from '../../services/global/aiAgentService';
import ContactDetailsDrawer from '../contacts/ContactDetailsDrawer';
import HandoffBanner from '../HandoffBanner';
import ContactChannelChips from '../contacts/ContactChannelChips';
import MessageText from '../MessageText';
import TransferContactModal from '../contacts/TransferContactModal';
import LabelPickerModal from '../labels/LabelPickerModal';
import ContactFeedbackModal from '../contacts/ContactFeedbackModal';
import SavedMessagesDrawer from '../messages/SavedMessagesDrawer';
import ForwardModal from '../messages/ForwardModal';
import {
  MessageActionMenu, ReactionChips, QuotedMessage, SelectionBar,
  messageActionCaps, whyCannotEdit,
} from '../messages';
import CreateNotificationModal from '../notifications/CreateNotificationModal';
import ScheduleMessageModal from '../followups/ScheduleMessageModal';
import FollowupBadge from '../followups/FollowupBadge';
import RequiredFieldsSignal from '../contacts/RequiredFieldsSignal';
import RequiredFieldsPrompt from '../contacts/RequiredFieldsPrompt';
import ErrorBoundary, { DrawerErrorFallback } from '../ErrorBoundary';
import { WABusinessAudioMessage, WABusinessVideoMessage } from '../WABusinessMedia';
import ThreadEventCard from '../chat/ThreadEventCard';
import StoryEventBubble, { isStoryEventMsg, isStoryPlaceholderText } from '../chat/StoryEventBubble';
import AdContextCard, { adContextOf } from '../chat/AdContextCard';
import { displayText } from '../../utils/messagePlaceholder';
import ToolUsageCard from '../chat/ToolUsageCard';
import useThreadEvents from '../../hooks/useThreadEvents';
import {
  AIWorkspace,
  MessageCheckbox,
  MessageRolePill,
  AttachedFilesPreview,
  useAIWorkspace,
  getWorkspaceColors,
  MAX_SELECTED_MESSAGES,
  ACTION_TYPES,
  extractMessagesPayload,
} from '../AIWorkspace';
import useAgentCheck from '../AIWorkspace/hooks/useAgentCheck';
import WorkspaceConfigDrawer from '../AIWorkspace/WorkspaceConfigDrawer';
import useWorkspaceConfig from '../AIWorkspace/hooks/useWorkspaceConfig';
import MessagesSkeleton from './MessagesSkeleton';
import useContactLabelsCount from '../../hooks/useContactLabelsCount';
import useTransferRoulettes from '../../hooks/useTransferRoulettes';

const PAGE_SIZE = 50;

const ChatWindow = ({
  adapter,
  chat, chats = [], account = null, onBack, isMobile, onMessageSent,
  autopilotContacts = [], autopilotMode = 'off', blockedContacts = [],
  activeAgentName = null,
  isHandoff = false, handoffMetadata = null,
  onResumeAgent = null,
}) => {
  const code = adapter.code;
  const prefix = adapter.prefix;
  const keyField = adapter.keyField;
  const service = adapter.service;
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const { createNotification } = useNotifications();
  const [messages, setMessages] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCreateNotification, setShowCreateNotification] = useState(false);
  const [showWorkspaceConfig, setShowWorkspaceConfig] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const contactRef = chat?.contact_ref
    || (chat?.[keyField] ? `${prefix}${chat[keyField]}` : '');
  const rawId = chat?.[keyField] || '';

  // Dispensa do banner de handoff (X): persiste por contato + motivo.
  const [handoffBannerDismissed, setHandoffBannerDismissed] = useState(false);
  const _handoffKey = rawId;
  const _handoffReason = handoffMetadata?.handoff_reason || handoffMetadata?.reason || '';
  useEffect(() => {
    try {
      const map = JSON.parse(localStorage.getItem(adapter.handoffDismissKey) || '{}');
      setHandoffBannerDismissed(map[_handoffKey] === (_handoffReason || '__'));
    } catch { setHandoffBannerDismissed(false); }
  }, [_handoffKey, _handoffReason, adapter.handoffDismissKey]);
  const dismissHandoffBanner = () => {
    setHandoffBannerDismissed(true);
    try {
      const map = JSON.parse(localStorage.getItem(adapter.handoffDismissKey) || '{}');
      map[_handoffKey] = _handoffReason || '__';
      localStorage.setItem(adapter.handoffDismissKey, JSON.stringify(map));
    } catch { /* localStorage indisponível — só não persiste */ }
  };

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);

  const contactLabels = useContactLabelsCount(contactRef);
  const workspaceConfigState = useWorkspaceConfig();
  const { roulettes: transferRoulettes, assignToRoulette } = useTransferRoulettes();
  const [rouletteBusyId, setRouletteBusyId] = useState(null);

  const aiWorkspace = useAIWorkspace();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  // "Encaminhar" direto do menu da bolha: encaminha SO aquela mensagem, sem
  // passar pela selecao do AI Workspace (que abre o painel ao selecionar).
  // null = o modal usa a selecao do workspace, como sempre fez.
  const [forwardOnlyIds, setForwardOnlyIds] = useState(null);
  const { handleAgentError, CreateAgentModal } = useAgentCheck();

  // Acoes de mensagem (responder citando, reagir, encaminhar, editar).
  // Tudo que o canal nao faz fica desabilitado com o motivo, pela matriz de
  // capacidades (utils/channels.js); nunca se compara o codigo do canal aqui.
  const { flags: featureFlags } = useFeatureFlags();
  const actionsOn = !!featureFlags?.message_actions;
  const actionCaps = useMemo(() => messageActionCaps(code), [code]);

  const audioRecorder = useAudioRecorder();
  const [sendingAudio, setSendingAudio] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  useEffect(() => {
    aiWorkspace.resetWorkspace();
    setShowForwardModal(false);
    setAttachedFiles([]);
    audioRecorder.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.id]);

  // Regra de midia do canal (adapter.normalizeMediaType devolve o tipo com
  // que o arquivo sai, ou null quando o canal nao aceita).
  const queueFiles = (incoming) => {
    const accepted = [];
    const rejected = [];
    (incoming || []).forEach((f) => {
      if (!f?.url) return;
      const type = adapter.normalizeMediaType(f.type);
      if (type) accepted.push({ ...f, type }); else rejected.push(f);
    });
    if (accepted.length) setAttachedFiles((prev) => [...prev, ...accepted]);
    if (rejected.length && adapter.texts.mediaRejected) setError(adapter.texts.mediaRejected);
    return accepted.length;
  };

  const handleRemoveAttachedFile = (index) => {
    if (sending) return;
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachedFiles = () => {
    if (sending) return;
    setAttachedFiles([]);
  };

  const chatIdRef = useRef(chat?.id);
  useEffect(() => { chatIdRef.current = chat?.id; }, [chat?.id]);

  // Responsável do contato, chaveado por contact_ref (nunca por telefone).
  const [assignedTo, setAssignedTo] = useState(null);
  const [gestorInternal, setGestorInternal] = useState(false);
  const [assignVersion, setAssignVersion] = useState(0);
  // "Responder novos contatos" só para contatos novos (campo agent_answers
  // do GET /assignment): contato antigo não mostra o agente no cabeçalho.
  const [agentAnswers, setAgentAnswers] = useState(true);
  useSSEEvent(SSE_EVENTS.CONTACT_ASSIGNMENT_CHANGED, useCallback((data) => {
    const ref = data?.contact_ref || data?.phone;
    if (ref && String(ref) === String(contactRef)) setAssignVersion((v) => v + 1);
  }, [contactRef]));
  useEffect(() => {
    if (!contactRef) { setAssignedTo(null); return undefined; }
    let cancelled = false;
    setAssignedTo(null);
    assignmentService.get(contactRef)
      .then((raw) => {
        if (cancelled) return;
        const d = raw?.data ?? raw;
        setAssignedTo(d?.assigned_to ?? null);
        setGestorInternal(!!d?.gestor_internal);
        setAgentAnswers(d?.agent_answers !== false);
      })
      .catch(() => { if (!cancelled) { setAssignedTo(null); setAgentAnswers(true); } });
    return () => { cancelled = true; };
  }, [contactRef, assignVersion]);
  const assignedMember = assignedTo?.type === 'member' ? assignedTo : null;
  const assignedAgent = assignedTo?.type === 'agent' ? assignedTo : null;
  const threadEvents = useThreadEvents(contactRef, assignedTo?.assigned_at || null);
  const messagesTimeline = useMemo(() => {
    const toSeconds = (t) => {
      let ms = new Date(t || 0).getTime();
      if (Number.isNaN(ms)) {
        const n = Number(t);
        if (!Number.isNaN(n)) ms = n < 10000000000 ? n * 1000 : n;
      }
      return Number.isNaN(ms) ? 0 : Math.floor(ms / 1000);
    };
    const firstTs = messages.length
      ? Math.min(...messages.map((m) => toSeconds(m.timestamp) || Infinity))
      : null;
    return [
      ...messages.map((m) => ({ item: m, ts: toSeconds(m.timestamp) })),
      ...threadEvents
        .filter((e) => firstTs === null || e.ts >= firstTs)
        .map((e) => ({ item: e, ts: e.ts })),
    ].sort((a, b) => a.ts - b.ts).map(({ item }) => item);
  }, [messages, threadEvents]);
  const diasDaConversa = useMemo(
    () => groupTimelineByDay(
      messagesTimeline,
      (item) => (item && item.timestamp !== undefined ? item.timestamp : item?.ts),
    ),
    [messagesTimeline],
  );
  const autopilotActive = (() => {
    const ref = String(contactRef || '').trim();
    if (!ref) return false;
    if (!agentAnswers) return false;
    return autopilotMode === 'all'
      ? !(blockedContacts || []).some((c) => String(c).trim() === ref)
      : (autopilotContacts || []).some((c) => String(c).trim() === ref);
  })();
  const [brokenMedia, setBrokenMedia] = useState(new Set());
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  // O campo cresce com o texto tambem quando o valor entra por codigo ("Usar
  // resposta" do agente, mensagem pronta): o onChange so roda ao digitar, e o
  // campo pode ser montado de novo quando o AI Workspace fecha. Mesma regra
  // dos shells de WhatsApp (caso do dono em 10/10/2026).
  const ajustarAlturaDoInput = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);
  const attachInputRef = useCallback((el) => {
    inputRef.current = el;
    ajustarAlturaDoInput(el);
  }, [ajustarAlturaDoInput]);
  useEffect(() => {
    ajustarAlturaDoInput(inputRef.current);
  }, [input, ajustarAlturaDoInput]);

  const windowOpen = adapter.windowed ? service.isWindowOpen(chat) : true;
  const displayName = service.getChatDisplayName(chat);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const cacheKeyRef = useRef(null);
  useEffect(() => {
    if (!chat?.id) { cacheKeyRef.current = null; return undefined; }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestedChatId = chat.id;

    let cancelled = false;
    setError(null);
    setBrokenMedia(new Set());
    const cacheOn = getFeatureFlag('chat_cache');
    const cached = cacheOn ? getChatEntry(code, requestedChatId) : null;
    cacheKeyRef.current = null;
    if (cached && Array.isArray(cached.messages)) {
      cacheKeyRef.current = requestedChatId;
      setMessages(cached.messages);
      setLoading(false);
    } else {
      setLoading(true);
      setMessages([]);
    }

    (async () => {
      try {
        const res = await service.getMessages(chat.id, PAGE_SIZE, 0, { signal: controller.signal });
        if (cancelled || controller.signal.aborted) return;
        // Backend devolve DESC (mais recente primeiro); a tela mostra ASC.
        const lista = [...(res?.data || [])].reverse();
        if (cacheOn) {
          cacheKeyRef.current = requestedChatId;
          setChatMessages(code, requestedChatId, lista);
        }
        setMessages(lista);
        service.markAsRead(chat.id).catch(() => {});
      } catch (e) {
        if (!cancelled && e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.error(`[${code}] falha ao carregar mensagens:`, e);
          setError('Não foi possível carregar as mensagens.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; controller.abort(); };
  }, [chat?.id, code, service]);

  useEffect(() => {
    const key = chat?.id;
    if (!key || cacheKeyRef.current !== key) return;
    if (!getFeatureFlag('chat_cache')) return;
    setChatEntry(code, key, { messages });
  }, [messages, chat?.id, code]);

  useEffect(() => { scrollToBottom(); }, [messages.length, scrollToBottom]);

  // Mensagem nova via SSE. Filtra por canal E por conversa.
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    if (data?.source !== code) return;

    if (data.type === 'message_deleted') {
      const mesmaConversa = data.chat_id
        ? data.chat_id === chat?.id
        : (data.contact?.[adapter.shortKey] || data[keyField]) === rawId;
      if (!mesmaConversa) return;
      setMessages((prev) => prev.map(
        (m) => (m.mid === data.mid ? { ...m, deleted: true } : m)
      ));
      return;
    }

    if (data?.chat_id !== chat?.id) return;

    const incoming = data.message;
    if (!incoming) return;
    setMessages((prev) => {
      if (prev.some((m) => m.id === incoming.id || (incoming.mid && m.mid === incoming.mid))) {
        return prev;
      }
      return [...prev, incoming];
    });
    service.markAsRead(chat.id).catch(() => {});
  }, [chat?.id, code, keyField, rawId, adapter.shortKey, service]);

  // Status (entregue/lido): por mid (canais da Meta) ou por lista de ids
  // (widget: {message_ids, status}).
  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    if (data?.source !== code || data?.chat_id !== chat?.id) return;
    if (Array.isArray(data.message_ids)) {
      const ids = new Set(data.message_ids);
      setMessages((prev) => prev.map(
        (m) => (ids.has(m.id) ? { ...m, status: data.status } : m)
      ));
      return;
    }
    setMessages((prev) => prev.map(
      (m) => (m.mid === data.mid ? { ...m, status: data.status } : m)
    ));
  }, [chat?.id, code]);

  useSSEEvent(SSE_EVENTS.MESSAGE_REACTION, (data) => {
    if (data?.source !== code || data?.chat_id !== chat?.id) return;
    setMessages((prev) => prev.map(
      (m) => (m.mid === data.mid ? { ...m, reaction: data.reaction || null } : m)
    ));
  }, [chat?.id, code]);

  // Enviar = texto (se houver) + CADA anexo da fila, em sequência.
  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    const filesToSend = [...attachedFiles];
    if ((!text && !filesToSend.length) || sending || !windowOpen) return;

    const sendChatId = chat.id;
    const sendChat = chat;
    const sameChat = () => chatIdRef.current === sendChatId;

    setSending(true);
    setError(null);
    try {
      if (text) {
        const res = await service.sendMessage(sendChatId, text);
        if (sameChat()) setInput('');
        if (res?.data && sameChat()) {
          setMessages((prev) => (
            prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]
          ));
        }
        onMessageSent?.(sendChatId, text);
      }
      for (let i = 0; i < filesToSend.length; i++) {
        const f = filesToSend[i];
        const res = await adapter.sendMedia(sendChat, f.url, f.type);
        if (res?.data && sameChat()) {
          setMessages((prev) => (
            prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]
          ));
        }
        onMessageSent?.(sendChatId, `[${f.type}]`);
        setAttachedFiles((prev) => prev.filter((p) => p !== f));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (sameChat()) {
        setError(
          (typeof detail === 'object' ? detail.message : detail)
          || 'Não foi possível enviar a mensagem.'
        );
      }
    } finally {
      setSending(false);
    }
  };

  // Gravacao de audio (voice note): so nos canais que aceitam audio.
  const handleStartRecording = async () => {
    if (!adapter.allowAudio || sending || sendingAudio || uploading || !windowOpen) return;
    setError(null);
    try {
      await audioRecorder.start();
    } catch (err) {
      setError(err.message || 'Não foi possível iniciar a gravação.');
    }
  };

  const handleCancelRecording = () => {
    audioRecorder.cancel();
  };

  const handleStopAndSendAudio = async () => {
    if (sendingAudio) return;
    const file = await audioRecorder.stop();
    if (!file) return;

    const sendChatId = chat.id;
    const sendChat = chat;
    const sameChat = () => chatIdRef.current === sendChatId;

    setSendingAudio(true);
    setError(null);
    try {
      const uploaded = await fileUploadService.uploadFile(file, null, { channel: code });
      const res = await adapter.sendMedia(sendChat, uploaded.url, 'audio');
      if (res?.data && sameChat()) {
        setMessages((prev) => (
          prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]
        ));
      }
      onMessageSent?.(sendChatId, '[audio]');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (sameChat()) {
        setError(
          (typeof detail === 'object' ? detail.message : detail)
          || 'Não foi possível enviar o áudio.'
        );
      }
    } finally {
      setSendingAudio(false);
    }
  };

  // ============================================
  // AI WORKSPACE — seleção de mensagens + ações
  // ============================================

  const isMessageBlocked = (msg) => {
    if (!msg || msg.deleted) return true;
    if (msg.media_url) return false;
    return !(msg.text && msg.text.trim());
  };

  const handleToggleMessageSelection = (msgId, msg) => {
    const isBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const isCurrentlySelected = aiWorkspace.selectedMessages.has(msgId);

    if (isCurrentlySelected) {
      aiWorkspace.toggleMessageSelection(msgId, msg);
      return;
    }
    if (isBlocked || isAtLimit) return;
    aiWorkspace.toggleMessageSelection(msgId, msg);
  };

  const getSelectedMessages = () => messages.filter((msg) => {
    const msgId = msg.id || msg.mid;
    return aiWorkspace.selectedMessages.has(msgId);
  });

  const selectedMessagesPreview = useMemo(() => {
    if (aiWorkspace.selectedMessages.size === 0) return [];
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const selectedMsgs = messages.filter((msg) => {
      const msgId = msg.id || msg.mid;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    const payload = extractMessagesPayload(selectedMsgs, { channel: code, chat, operatorName });
    return payload.map((p) => ({
      role: p.role,
      text: p.text,
      type: p.type,
      senderLabel: p.role === 'operator' ? 'Você' : (p.sender_name || 'Cliente'),
      timestamp: p.timestamp,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiWorkspace.selectedMessages, messages, chat]);

  const handleExecuteTranslate = async () => {
    if (!input.trim()) return;
    const originalText = input.trim();
    try {
      await aiWorkspace.executeTranslate(originalText, async ({ action, text, languages }) => {
        const results = await aiAgentService.processAction({ action, text, languages });
        return results.map((r) => ({ ...r, originalText }));
      });
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Translate error:`, err);
    }
  };

  const handleExecuteGenerate = async () => {
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const messagesPayload = extractMessagesPayload(getSelectedMessages(), { channel: code, chat, operatorName });
    if (messagesPayload.length === 0) return;

    const contextWithMessages = {
      chatId: chat?.id,
      chatName: displayName,
      contact_phone: contactRef,
      channel: code,
      operatorName,
      originalMessages: messagesPayload,
    };

    try {
      await aiWorkspace.executeGenerate(messagesPayload, async ({ action, messages: msgs }) => {
        const results = await aiAgentService.processAction({
          action, messages: msgs, languages: [], context: contextWithMessages,
        });
        return results.map((r) => ({ ...r, originalMessages: messagesPayload }));
      }, contextWithMessages);
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Generate error:`, err);
    }
  };

  const handleExecuteCustom = async (prompt, files = []) => {
    if (!prompt?.trim()) return;
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const originalPrompt = prompt.trim();
    const originalFiles = Array.isArray(files) ? files : [];
    const refMessages = extractMessagesPayload(getSelectedMessages(), { channel: code, chat, operatorName });

    const baseContext = {
      userId: userMetadata.id || 'anonymous',
      chatId: chat?.id,
      chatName: displayName,
      contact_phone: contactRef,
      channel: code,
      operatorName,
      messages: refMessages,
    };

    try {
      await aiWorkspace.executeCustom(originalPrompt, async ({ action, prompt: userPrompt }) => {
        const results = await aiAgentService.processAction({
          action, prompt: userPrompt, files: originalFiles, context: baseContext,
        });
        return results.map((r) => ({ ...r, originalPrompt, originalFiles }));
      }, baseContext);
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Custom prompt error:`, err);
    }
  };

  const handleRegenerateTranslate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.TRANSLATE) <= 0) return;
    setRegeneratingId(result.id);
    try {
      const originalText = result.originalText || result.text;
      await aiWorkspace.regenerateResult(
        result.id, ACTION_TYPES.TRANSLATE,
        async (params) => {
          const results = await aiAgentService.processAction({ ...params, text: originalText });
          return results.map((r) => ({ ...r, originalText }));
        },
        { text: originalText, originalText }
      );
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Regenerate translate error:`, err);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleRegenerateGenerate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.GENERATE) <= 0) return;
    setRegeneratingId(result.id);
    try {
      const originalMessages = result.originalMessages || [];
      if (originalMessages.length === 0) return;
      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
      await aiWorkspace.regenerateResult(
        result.id, ACTION_TYPES.GENERATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params, messages: originalMessages,
            context: { chatId: chat?.id, chatName: displayName, contact_phone: contactRef, channel: code, operatorName },
          });
          return results.map((r) => ({ ...r, originalMessages }));
        },
        { originalMessages }
      );
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Regenerate generate error:`, err);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleRegenerateCustom = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.CUSTOM) <= 0) return;
    setRegeneratingId(result.id);
    try {
      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
      const originalPrompt = result.originalPrompt || '';
      if (!originalPrompt) return;
      await aiWorkspace.regenerateResult(
        result.id, ACTION_TYPES.CUSTOM,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params, prompt: originalPrompt,
            context: { userId: userMetadata.id || 'anonymous', chatId: chat?.id, chatName: displayName, contact_phone: contactRef, channel: code, operatorName },
          });
          return results.map((r) => ({ ...r, originalPrompt }));
        },
        { originalPrompt }
      );
    } catch (err) {
      if (handleAgentError(err)) return;
      console.error(`[${code} ChatWindow] Regenerate custom error:`, err);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleUseResponse = (result) => {
    if (result?.text) setInput(renderVariablesInText(result.text, {
      contactName: displayName,
      operatorName: user?.name,
    }));
    if (result && (Array.isArray(result.attachments) || result.audio)) {
      const incoming = [];
      (result.attachments || []).forEach((a) => {
        if (!a?.file_url) return;
        incoming.push({ url: a.file_url, filename: a.file_name || 'arquivo', type: a.type || 'file', mimeType: a.mime_type, size: a.file_size });
      });
      if (result.audio?.file_url) {
        incoming.push({ url: result.audio.file_url, filename: result.audio.file_name || 'audio.webm', type: 'audio', mimeType: result.audio.mime_type || 'audio/webm' });
      }
      if (incoming.length) queueFiles(incoming);
      return;
    }
    if (result?.files?.length) queueFiles(result.files);
  };

  const handleExecuteRoulette = async (groupId) => {
    if (!contactRef || rouletteBusyId) return;
    setRouletteBusyId(groupId);
    try {
      await assignToRoulette(contactRef, groupId);
    } catch (err) {
      console.error(`[${code} ChatWindow] roleta:`, err);
    } finally {
      setRouletteBusyId(null);
    }
  };

  // ============================================
  // ENCAMINHAR — reusa a seleção do workspace
  // ============================================

  const extractForwardable = (msg) => {
    if (!msg || msg.deleted) return null;
    if (msg.media_url) {
      const kind = adapter.normalizeMediaType(msg.media_type || msg.type);
      if (!kind) return null;
      return { mediaUrl: msg.media_url, mediaType: kind };
    }
    if (msg.text && msg.text.trim()) return { text: msg.text.trim() };
    return null;
  };

  const forwardDestinations = useMemo(() => {
    const seen = new Set();
    const out = [];
    (chats || []).forEach((c) => {
      const cid = c?.[keyField];
      if (!cid || cid === rawId) return;
      if (seen.has(cid)) return;
      seen.add(cid);
      out.push({
        id: c.id,
        name: service.getChatDisplayName(c),
        phone: adapter.hasHandle && c.contact_username ? `@${c.contact_username}` : '',
        avatarUrl: c.contact_profile_picture_url || null,
        _chat: c,
        _chatId: c.id,
      });
    });
    return out;
  }, [chats, rawId, keyField, service, adapter.hasHandle]);

  // Ids que o ForwardModal encaminha: a mensagem unica do menu da bolha ou a
  // selecao do workspace.
  const forwardIds = forwardOnlyIds || aiWorkspace.selectedMessages;
  const closeForwardModal = () => {
    setShowForwardModal(false);
    setForwardOnlyIds(null);
  };

  const handleForwardSend = async (selectedDestinations) => {
    const toForward = messages
      .filter((msg) => forwardIds.has(msg.id || msg.mid))
      .map((msg) => extractForwardable(msg))
      .filter(Boolean);

    const outcomes = [];
    for (const dest of selectedDestinations) {
      let ok = true;
      try {
        for (const item of toForward) {
          if (item.text) {
            await service.sendMessage(dest._chatId, item.text);
          } else if (item.mediaUrl) {
            await adapter.sendMedia(dest._chat, item.mediaUrl, item.mediaType);
          }
        }
      } catch (err) {
        console.error(`[${code} ChatWindow] Forward failed for`, dest.name, err);
        ok = false;
      }
      outcomes.push({ id: dest.id, name: dest.name, ok });
    }
    return outcomes;
  };

  // Bloco de citacao da bolha: o backend ja manda `quoted` nas respostas que
  // o contato faz "em cima" de uma mensagem (Instagram/Messenger). Mensagem
  // antiga so tem `reply_to_mid`: monta a citacao a partir da original na
  // lista, quando ela esta carregada.
  const quotedOf = (msg) => {
    if (msg.quoted) return msg.quoted;
    if (!msg.reply_to_mid) return null;
    const orig = messages.find((m) => m.mid && m.mid === msg.reply_to_mid);
    if (!orig) return null;
    return {
      id: orig.id || null,
      external_id: orig.mid,
      text: orig.deleted ? 'Mensagem apagada' : ((orig.text || '').trim() || null),
      from_me: !!orig.from_me,
      media_type: orig.media_url ? (orig.media_type || null) : null,
      sender_name: null,
    };
  };

  // Clicar na citacao rola ate a mensagem original e a destaca por 2 s.
  const jumpToQuoted = (quoted) => {
    if (!quoted) return;
    const target = messages.find((m) => (
      (quoted.id && m.id === quoted.id) || (quoted.external_id && m.mid === quoted.external_id)
    ));
    if (!target) return;
    const el = document.getElementById(`msg-${target.id || target.mid}`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('msg-row--flash');
    setTimeout(() => el.classList.remove('msg-row--flash'), 2000);
  };

  const timestampMs = (ts) => {
    if (!ts) return 0;
    if (typeof ts === 'number') return ts < 1e12 ? ts * 1000 : ts;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const copyMessageText = (msg) => {
    const text = (msg.text || '').trim();
    if (!text || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const styles = {
    container: { flex: 1, display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: colors.bgSecondary, overflow: 'hidden' },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: isMobile ? '4px' : '8px', padding: isMobile ? '8px 8px' : '10px 16px',
      ...(isMobile && { paddingTop: 'calc(8px + env(safe-area-inset-top))' }),
      backgroundColor: colors.bgPrimary, boxShadow: colors.shadowSm, minHeight: '60px', zIndex: 1, flexShrink: 0,
    },
    headerInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
    headerAvatar: { width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    avatarPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.bgTertiary, color: colors.textTertiary },
    headerDetails: { display: 'flex', flexDirection: 'column' },
    headerName: { fontWeight: 500, fontSize: '16px', color: colors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? '46vw' : 'none' },
    headerStatus: { fontSize: '13px', color: colors.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    headerActions: { display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', flexShrink: 0 },
    headerActionBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', border: 'none', background: 'transparent', color: colors.textSecondary, borderRadius: '50%', cursor: 'pointer' },
    headerMenuItem: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 14px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '13px', color: colors.textPrimary, transition: 'background-color 0.1s' },
    messages: { flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6, backgroundColor: colors.bgChat },
    bubbleBase: { maxWidth: '68%', padding: '8px 12px', borderRadius: 10, fontSize: 14, lineHeight: 1.45, wordBreak: 'break-word' },
    messageRow: { display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0', borderRadius: '8px', transition: 'background-color 0.15s ease' },
    messageRowSelectedSent: { backgroundColor: `${workspaceColors.roleOperator}15`, margin: '2px -8px', padding: '6px 8px', borderRight: `3px solid ${workspaceColors.roleOperator}` },
    messageRowSelectedReceived: { backgroundColor: `${workspaceColors.roleCustomer}15`, margin: '2px -8px', padding: '6px 8px', borderLeft: `3px solid ${workspaceColors.roleCustomer}` },
    messageBubbleWrapper: { flex: 1, display: 'flex', position: 'relative', minWidth: 0 },
    composer: { display: 'flex', alignItems: 'flex-end', gap: 10, padding: '12px 16px', backgroundColor: colors.bgPrimary, borderTop: `1px solid ${colors.borderColor}`, flexShrink: 0 },
    textarea: { flex: 1, resize: 'none', border: 'none', outline: 'none', backgroundColor: colors.bgSecondary, color: colors.textPrimary, borderRadius: 20, padding: '10px 16px', fontSize: 16, maxHeight: 120, fontFamily: 'inherit' },
    sendBtn: { width: 42, height: 42, borderRadius: '50%', border: 'none', flexShrink: 0, backgroundColor: colors.accentPrimary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    closedNotice: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 18px', backgroundColor: colors.bgPrimary, borderTop: `1px solid ${colors.borderColor}`, color: colors.textSecondary, fontSize: 13, lineHeight: 1.5, flexShrink: 0 },
    errorBar: { padding: '8px 16px', backgroundColor: 'rgba(239,68,68,0.12)', color: '#dc2626', fontSize: 13, flexShrink: 0 },
  };

  const renderMessage = (msg) => {
    if (msg.tool_event || msg.type === 'tool_event') {
      return (
        <ToolUsageCard
          key={msg.id || msg.mid}
          toolEvent={msg.tool_event}
          agentName={msg.agent_name}
          timestamp={msg.timestamp}
          colors={colors}
        />
      );
    }
    const fromMe = msg.from_me;
    const msgId = msg.id || msg.mid;
    const isSelected = aiWorkspace.selectedMessages.has(msgId);
    const isHovered = hoveredMessage === msgId;
    const showCheckbox = isHovered || isSelected || aiWorkspace.selectedMessages.size > 0;
    const msgIsBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const selectedRowStyle = isSelected
      ? (fromMe ? styles.messageRowSelectedSent : styles.messageRowSelectedReceived)
      : {};
    const contactFirstName = (displayName || 'Cliente').split(' ')[0];

    const isAgentMsg = msg.source === 'agent_autopilot'
      || msg.agent_response || msg.originated_by_agent || !!msg.agent_id;
    const isSystemMsg = msg.source === 'system' || msg.type === 'system';
    const senderName = fromMe
      ? (isAgentMsg
          ? (msg.agent_name || msg.sent_by_name || activeAgentName || 'Agente IA')
          : (isSystemMsg ? 'Sistema' : (msg.sent_by_name || user?.name || null)))
      : null;
    const senderShort = senderName ? senderName.split(' ')[0] : null;

    const bubbleStyle = {
      ...styles.bubbleBase,
      backgroundColor: fromMe ? colors.messageSent : colors.messageReceived,
      color: fromMe ? colors.messageSentText : colors.messageReceivedText,
      ...(isSystemMsg ? { opacity: 0.85, fontStyle: 'italic' } : {}),
      // Com as acoes ligadas a bolha vive numa coluna (bolha + chips de
      // reacao) que ja limita a largura; o gatilho do menu e absoluto.
      ...(actionsOn ? { maxWidth: '100%', position: 'relative' } : {}),
    };

    const quoted = quotedOf(msg);
    const hasText = !!(msg.text && msg.text.trim());
    const hasMedia = !!msg.media_url;
    const editedLabel = msg.edited ? (
      <span style={{ fontStyle: 'italic' }} title="Mensagem editada">editada</span>
    ) : null;

    // Menu "v" da bolha (flag message_actions). Responder/Reagir/Editar nao
    // existem nos canais deste shell: ficam desabilitados com o motivo da
    // matriz. O menu compartilhado esconde item SEM handler, entao passa-se
    // um handler inerte quando o item esta desabilitado, para ele aparecer.
    const noop = () => {};
    const editReason = whyCannotEdit({
      fromMe, hasText, hasMedia, timestampMs: timestampMs(msg.timestamp), deleted: !!msg.deleted,
    }, actionCaps);
    const forwardReason = !actionCaps.forward
      ? 'Este canal não encaminha mensagem'
      : (msgIsBlocked ? 'Mensagem sem conteúdo para encaminhar' : '');
    const actionMenu = actionsOn ? (
      <MessageActionMenu
        fromMe={fromMe}
        colors={colors}
        caps={actionCaps}
        editReason={editReason}
        hasText={hasText && !msg.deleted}
        currentReaction={null}
        forwardReason={forwardReason}
        alwaysVisible={!!isMobile}
        onReply={actionCaps.quote ? undefined : noop}
        onReact={actionCaps.reactions ? undefined : noop}
        onForward={() => {
          if (forwardReason) return;
          setForwardOnlyIds(new Set([msgId]));
          setShowForwardModal(true);
        }}
        onSelect={() => handleToggleMessageSelection(msgId, msg)}
        onCopy={() => copyMessageText(msg)}
        onEdit={editReason ? noop : undefined}
      />
    ) : null;

    const bubble = msg.deleted ? (
      <div style={{ ...bubbleStyle, fontStyle: 'italic', opacity: 0.65 }}>
        Mensagem apagada
        {actionMenu}
      </div>
    ) : (
      <div style={bubbleStyle}>
        {actionMenu}
        {fromMe && (
          <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4, paddingRight: actionsOn ? 22 : 0 }}>
            <span>{service.formatTime(msg.timestamp)}</span>
            {editedLabel}
            {isAgentMsg && <Bot size={11} style={{ flexShrink: 0 }} />}
            {senderShort && (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {senderShort}
              </span>
            )}
            <MessageStatus status={msg.status} channel={code} size={12} />
          </div>
        )}
        {quoted && (
          <QuotedMessage
            quoted={quoted}
            contactName={displayName}
            colors={colors}
            fromMe={fromMe}
            onJump={jumpToQuoted}
          />
        )}
        {adContextOf(msg) && !msg.from_me && (
          <AdContextCard ad={adContextOf(msg)} colors={colors} />
        )}
        {isStoryEventMsg(msg) && (
          <StoryEventBubble msg={msg} colors={colors} />
        )}
        {!isStoryEventMsg(msg) && msg.media_url && (
          msg.media_type === 'image' ? (
            brokenMedia.has(msg.id) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7, fontSize: 12, marginBottom: msg.text ? 6 : 0 }}>
                <ImageOff size={13} />
                Mídia não está mais disponível
              </div>
            ) : (
              <img
                src={msg.media_url}
                alt=""
                style={{ maxWidth: '100%', borderRadius: 6, marginBottom: msg.text ? 6 : 0 }}
                onError={() => setBrokenMedia((prev) => {
                  const next = new Set(prev);
                  next.add(msg.id);
                  return next;
                })}
              />
            )
          ) : msg.media_type === 'audio' ? (
            <WABusinessAudioMessage
              mediaUrl={msg.media_url}
              isSent={fromMe}
              wamid={msg.mid || msg.id}
              chatId={chat.id}
              channel={code}
              initialTranscription={msg.transcription}
            />
          ) : msg.media_type === 'video' ? (
            brokenMedia.has(msg.id) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7, fontSize: 12, marginBottom: msg.text ? 6 : 0 }}>
                <ImageOff size={13} />
                Mídia não está mais disponível
              </div>
            ) : (
              <div style={{ marginBottom: msg.text ? 6 : 0 }}>
                <WABusinessVideoMessage
                  mediaUrl={msg.media_url}
                  isSent={fromMe}
                  onMediaError={() => setBrokenMedia((prev) => {
                    const next = new Set(prev);
                    next.add(msg.id);
                    return next;
                  })}
                />
              </div>
            )
          ) : (
            <a
              href={msg.media_url}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              {(msg.media_type === 'file' || msg.type === 'file') && (
                <FileText size={13} style={{ flexShrink: 0 }} />
              )}
              {msg.media_filename || `Abrir ${msg.media_type || 'anexo'}`}
            </a>
          )
        )}
        {msg.text && !(isStoryEventMsg(msg) && isStoryPlaceholderText(msg.text))
          && displayText(msg.text, { hasMedia: !!msg.media_url }) && (
          <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap', minWidth: 0 }}>
            <MessageText text={displayText(msg.text, { hasMedia: !!msg.media_url })} />
          </div>
        )}
        {/* O que so este canal renderiza (botoes interativos do widget). */}
        {adapter.renderBubbleExtra?.(msg, { colors, fromMe })}
        {(!fromMe || (msg.reaction && !actionsOn)) && (
          <div style={{ fontSize: 10, opacity: 0.7, marginTop: 3, textAlign: 'right', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
            {!fromMe && <span>{service.formatTime(msg.timestamp)}</span>}
            {!fromMe && editedLabel}
            {msg.reaction && !actionsOn && <span>{msg.reaction}</span>}
          </div>
        )}
      </div>
    );

    // Com as acoes ligadas, a reacao do contato vira chip abaixo da bolha
    // (ReactionChips, so leitura: nenhum canal deste shell envia reacao).
    const bubbleColumn = actionsOn ? (
      <div style={{
        display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '68%',
        alignItems: fromMe ? 'flex-end' : 'flex-start',
      }}>
        {bubble}
        <ReactionChips
          reaction={msg.deleted ? null : msg.reaction}
          reactionFromMe={null}
          align={fromMe ? 'right' : 'left'}
          colors={colors}
        />
      </div>
    ) : bubble;

    return (
      <div
        key={msgId}
        id={`msg-${msgId}`}
        className={actionsOn ? `msg-row${isSelected ? ' msg-row--selected' : ''}` : undefined}
        style={{ ...styles.messageRow, ...selectedRowStyle }}
        onMouseEnter={() => setHoveredMessage(msgId)}
        onMouseLeave={() => setHoveredMessage(null)}
      >
        <MessageCheckbox
          isSelected={isSelected}
          isVisible={showCheckbox}
          onToggle={() => handleToggleMessageSelection(msgId, msg)}
          isBlocked={msgIsBlocked}
          isAtLimit={isAtLimit && !isSelected}
          blockReason={msgIsBlocked ? 'Mensagem sem conteúdo selecionável' : ''}
        />
        <div style={{ ...styles.messageBubbleWrapper, justifyContent: fromMe ? 'flex-end' : 'flex-start' }}>
          {isSelected && (
            <MessageRolePill
              role={fromMe ? 'operator' : 'customer'}
              label={fromMe ? 'Sua mensagem' : contactFirstName}
              side={fromMe ? 'right' : 'left'}
            />
          )}
          {bubbleColumn}
        </div>
      </div>
    );
  };

  if (!chat) return null;

  const headerStatus = adapter.headerStatus?.(chat, account) || adapter.label;
  const HeaderExtra = adapter.HeaderExtra;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {isMobile && onBack && (
          <button type="button" onClick={onBack} title="Voltar" style={{ ...styles.headerActionBtn, color: colors.textPrimary, flexShrink: 0 }}>
            <ArrowLeft size={24} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowDrawer(true)}
          title="Ver dados do contato"
          style={{
            ...styles.headerInfo, background: 'transparent', border: 'none', padding: '2px 4px', borderRadius: 8,
            cursor: 'pointer', textAlign: 'left', flex: isMobile ? 1 : undefined, minWidth: isMobile ? 0 : undefined,
          }}
        >
          <div style={styles.headerAvatar}>
            {chat.contact_profile_picture_url ? (
              <img src={chat.contact_profile_picture_url} alt="" style={styles.avatarImg} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div style={styles.avatarPlaceholder}>
                <User size={24} />
              </div>
            )}
          </div>
          <div style={{ ...styles.headerDetails, minWidth: isMobile ? 0 : undefined }}>
            <span style={styles.headerName}>{displayName}</span>
            <span style={styles.headerStatus}>{headerStatus}</span>
            {gestorInternal ? (
              <span style={{ fontSize: '11px', color: colors.accentPrimary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Bot size={10} />
                Gestor ativo para a equipe interna
              </span>
            ) : assignedMember ? (
              <span style={{ fontSize: '11px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Bot size={10} />
                {`Em atendimento com ${assignedMember.name || 'membro da equipe'}`}
              </span>
            ) : (assignedAgent && autopilotActive) ? (
              <span style={{ fontSize: '11px', color: colors.accentPrimary, opacity: 0.85, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Bot size={10} />
                {assignedAgent.name || 'Agente IA'}
              </span>
            ) : autopilotActive ? (
              <span style={{ fontSize: '11px', color: colors.accentPrimary, opacity: 0.85, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Bot size={10} />
                {activeAgentName || 'Agente IA'}
              </span>
            ) : null}
          </div>
        </button>
        <div style={styles.headerActions}>
          <RequiredFieldsSignal contactPhone={contactRef} colors={colors} refreshKey={contactLabels.count} onClick={() => setShowDrawer(true)} />
          <RequiredFieldsPrompt contactKey={contactRef} colors={colors} />
          <FollowupBadge contactPhone={contactRef} colors={colors} refreshKey={scheduleVersion} onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')} />
          {/* Icone escondido de encaminhar: so no caminho antigo. Com a flag
              a acao mora na SelectionBar, no lugar do composer. */}
          {!actionsOn && aiWorkspace.selectedMessages.size > 0 && (
            <button
              onClick={() => setShowForwardModal(true)}
              style={styles.headerActionBtn}
              title={`Encaminhar ${aiWorkspace.selectedMessages.size} ${aiWorkspace.selectedMessages.size === 1 ? 'mensagem' : 'mensagens'}`}
            >
              <Forward size={20} />
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowHeaderMenu(!showHeaderMenu)} style={styles.headerActionBtn} title="Mais opções">
              <MoreVertical size={20} />
            </button>
            {showHeaderMenu && (
              <>
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={() => setShowHeaderMenu(false)} />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0, backgroundColor: colors.bgPrimary, borderRadius: '10px',
                  border: `1px solid ${colors.borderColor}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '210px', overflow: 'hidden', padding: '6px 0',
                }}>
                  {[
                    { icon: <History size={15} color={colors.accentPrimary} />, label: 'Mensagens Prontas', onClick: () => setShowSaved(true) },
                    { icon: <Zap size={15} color={colors.accentPrimary} />, label: 'Agendar Follow-up', onClick: () => setShowScheduleModal(true) },
                    { icon: <Tags size={15} color={colors.accentPrimary} />, label: contactLabels.hasLabels ? 'Alterar funil/etapa' : 'Definir funil/etapa', onClick: () => setShowLabelPicker(true) },
                    { icon: <UserCheck size={15} color="#f59e0b" />, label: 'Transferir contato', onClick: () => setShowTransfer(true) },
                    { icon: <MessageSquarePlus size={15} color="#10B981" />, label: 'Adicionar comentário', onClick: () => setShowFeedback(true) },
                    { icon: <Bell size={15} color={colors.accentPrimary} />, label: 'Criar notificação', onClick: () => setShowCreateNotification(true) },
                  ].map((item) => (
                    <button
                      key={item.label}
                      onClick={() => { setShowHeaderMenu(false); item.onClick(); }}
                      style={styles.headerMenuItem}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                  {adapter.headerMenuExtra?.({ chat, account, close: () => setShowHeaderMenu(false), styles, colors })}
                  <div style={{ height: 1, backgroundColor: colors.borderColor, margin: '6px 14px' }} />
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowWorkspaceConfig(true); }}
                    style={styles.headerMenuItem}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Settings size={15} color={colors.textSecondary} />
                    Configurar Workspace
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {HeaderExtra && <HeaderExtra chat={chat} account={account} colors={colors} />}

      <ContactChannelChips variant="bar" contactKey={contactRef} channel={code} colors={colors} reloadKey={assignVersion} />

      {isHandoff && !handoffBannerDismissed && (
        <HandoffBanner
          theme={theme}
          colors={colors}
          agentName={handoffMetadata?.handoff_agent_name || activeAgentName}
          reason={handoffMetadata?.handoff_reason || handoffMetadata?.reason}
          // Handoff sem destino vai para um humano (admin ou o proprio
          // usuario): o evento chega antes da atribuicao, entao o nome vem
          // do responsavel quando o metadata nao traz.
          targetName={handoffMetadata?.handoff_target_name || assignedMember?.name}
          onAddComment={() => setShowFeedback(true)}
          onResume={() => onResumeAgent?.(contactRef)}
          onDismiss={dismissHandoffBanner}
        />
      )}

      <div style={styles.messages}>
        {loading && messages.length === 0 ? (
          <MessagesSkeleton />
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: colors.textSecondary, padding: 30 }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Carregando mensagens...
          </div>
        ) : (
          diasDaConversa.map(({ rotulo, itens }) => (
            <React.Fragment key={`dia-${rotulo || 'sem-data'}`}>
              <ChatDateDivider label={rotulo} />
              {itens.map((msg) => (
                msg.__threadEvent
                  ? <ThreadEventCard key={msg.id} event={msg.event} colors={colors} />
                  : renderMessage(msg)
              ))}
            </React.Fragment>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <ForwardModal
        isOpen={showForwardModal}
        onClose={closeForwardModal}
        messageCount={forwardIds.size}
        destinations={forwardDestinations}
        onForward={handleForwardSend}
        colors={colors}
      />

      <AIWorkspace
        isOpen={aiWorkspace.isWorkspaceOpen}
        onToggleWorkspace={aiWorkspace.toggleWorkspace}
        selectedCount={aiWorkspace.selectedMessages.size}
        inputText={input}

        activeDropdown={aiWorkspace.activeDropdown}
        onToggleDropdown={aiWorkspace.toggleDropdown}
        onCloseDropdown={aiWorkspace.closeDropdown}

        translateLanguages={aiWorkspace.translateLanguages}
        onToggleTranslateLanguage={aiWorkspace.toggleTranslateLanguage}
        translateResults={aiWorkspace.translateResults}
        translateProcessing={aiWorkspace.translateProcessing}
        translateError={aiWorkspace.translateError}
        hasTranslateResults={aiWorkspace.hasTranslateResults}

        generateResults={aiWorkspace.generateResults}
        generateProcessing={aiWorkspace.generateProcessing}
        generateError={aiWorkspace.generateError}
        hasGenerateResults={aiWorkspace.hasGenerateResults}

        customResults={aiWorkspace.customResults}
        customProcessing={aiWorkspace.customProcessing}
        customError={aiWorkspace.customError}
        hasCustomResults={aiWorkspace.hasCustomResults}

        historyItems={aiWorkspace.historyItems}
        isHistoryOpen={aiWorkspace.isHistoryOpen}
        onToggleHistory={aiWorkspace.toggleHistory}
        onRemoveFromHistory={aiWorkspace.removeFromHistory}
        onClearHistory={aiWorkspace.clearHistory}
        hasHistory={aiWorkspace.hasHistory}

        workspaceConfig={workspaceConfigState.config}
        onOpenSavedMessages={() => setShowSaved(true)}
        onOpenLabels={() => setShowLabelPicker(true)}
        onOpenFollowup={() => setShowScheduleModal(true)}
        onOpenFeedback={() => setShowFeedback(true)}
        onOpenWorkspaceConfig={() => setShowWorkspaceConfig(true)}

        onClear={aiWorkspace.clearSelection}
        onExecuteTranslate={handleExecuteTranslate}
        onExecuteGenerate={handleExecuteGenerate}
        onExecuteCustom={handleExecuteCustom}
        onUseResponse={handleUseResponse}
        onRegenerateTranslate={handleRegenerateTranslate}
        onRegenerateGenerate={handleRegenerateGenerate}
        onRegenerateCustom={handleRegenerateCustom}
        onRemoveTranslateResult={aiWorkspace.removeTranslateResult}
        onRemoveGenerateResult={aiWorkspace.removeGenerateResult}
        onRemoveCustomResult={aiWorkspace.removeCustomResult}

        getTranslateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.TRANSLATE)}
        getGenerateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.GENERATE)}
        getCustomRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.CUSTOM)}
        regeneratingId={regeneratingId}

        selectedMessagesPreview={selectedMessagesPreview}

        transferRoulettes={transferRoulettes}
        onExecuteRoulette={handleExecuteRoulette}
        rouletteProcessingId={rouletteBusyId}
      />

      {error && <div style={styles.errorBar}>{error}</div>}

      {/* Com mensagens selecionadas (flag message_actions) a barra de selecao
          toma o lugar do composer, inclusive com a janela de 24 h fechada:
          encaminhar para outra conversa continua permitido. */}
      {actionsOn && aiWorkspace.selectedMessages.size > 0 ? (
        <SelectionBar
          count={aiWorkspace.selectedMessages.size}
          max={MAX_SELECTED_MESSAGES}
          onForward={() => { setForwardOnlyIds(null); setShowForwardModal(true); }}
          onAskAgent={aiWorkspace.openWorkspace}
          onCancel={aiWorkspace.clearSelection}
          colors={colors}
          forwardDisabledReason={actionCaps.forward ? '' : 'Este canal não encaminha mensagem'}
        />
      ) : windowOpen ? (
        <>
        <AttachedFilesPreview files={attachedFiles} onRemoveFile={handleRemoveAttachedFile} onClearAll={handleClearAttachedFiles} />
        <form style={styles.composer} onSubmit={handleSend}>
          {audioRecorder.isRecording ? (
            <RecordingBar seconds={audioRecorder.seconds} sending={sendingAudio} onCancel={handleCancelRecording} onSend={handleStopAndSendAudio} colors={colors} />
          ) : (
            <>
              <AttachButton
                channel={code}
                disabled={!windowOpen}
                allowDocument={adapter.allowDocument}
                buttonStyle={{
                  width: 42, height: 42, borderRadius: '50%', border: 'none', flexShrink: 0,
                  backgroundColor: 'transparent', color: colors.textSecondary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
                onUploaded={queueFiles}
                onError={setError}
                onUploadingChange={setUploading}
              />
              <textarea
                ref={attachInputRef}
                style={styles.textarea}
                rows={1}
                placeholder="Escreva uma mensagem"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); }
                }}
              />
              {(input.trim() || attachedFiles.length > 0 || !adapter.allowAudio) ? (
                <button
                  type="submit"
                  style={{ ...styles.sendBtn, opacity: !sending && (input.trim() || attachedFiles.length) ? 1 : 0.5, cursor: !sending ? 'pointer' : 'default' }}
                  disabled={sending || !(input.trim() || attachedFiles.length)}
                >
                  {sending ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleStartRecording}
                  title="Gravar áudio"
                  style={{ ...styles.sendBtn, opacity: sending || sendingAudio || uploading ? 0.5 : 1, cursor: sending || sendingAudio || uploading ? 'default' : 'pointer' }}
                  disabled={sending || sendingAudio || uploading}
                >
                  {sendingAudio ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Mic size={18} />}
                </button>
              )}
            </>
          )}
        </form>
        </>
      ) : (
        <div style={styles.closedNotice}>
          <Clock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{adapter.texts.closedNotice(displayName)}</span>
          <ContactChannelChips variant="cta" contactKey={contactRef} channel={code} colors={colors} style={{ marginTop: 0, flexShrink: 0 }} />
        </div>
      )}

      <ErrorBoundary
        name="contact-drawer"
        resetKeys={[showDrawer, contactRef]}
        fallback={({ reset }) => (showDrawer ? (
          <DrawerErrorFallback colors={colors} onClose={() => { reset(); setShowDrawer(false); }} />
        ) : null)}
      >
        <ContactDetailsDrawer
          isOpen={showDrawer}
          onClose={() => setShowDrawer(false)}
          colors={colors}
          source={code}
          channel={code}
          contactKey={contactRef}
          companyContactId={chat.company_contact_id || null}
          contactPhone={contactRef}
          contactName={displayName}
          contactProfilePicUrl={chat.contact_profile_picture_url}
          chatId={chat.id}
          onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')}
        />
      </ErrorBoundary>

      <TransferContactModal isOpen={showTransfer} onClose={() => setShowTransfer(false)} contactPhone={contactRef} contactName={displayName} colors={colors} displayMode="drawer" />

      <LabelPickerModal isOpen={showLabelPicker} onClose={() => { setShowLabelPicker(false); contactLabels.reload(); }} contactPhone={contactRef} contactName={displayName} colors={colors} displayMode="drawer" />

      <ScheduleMessageModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        contactPhone={contactRef}
        contactName={displayName}
        colors={colors}
        channel={code}
        onScheduled={() => setScheduleVersion((v) => v + 1)}
        displayMode="drawer"
      />

      <CreateNotificationModal
        isOpen={showCreateNotification}
        onClose={() => setShowCreateNotification(false)}
        contactName={displayName}
        onSubmit={async ({ title, body, scheduled_at }) => {
          await createNotification({
            title, body, scheduled_at,
            contact_phone: contactRef || null,
            chat_id: chat?.id || null,
            channel: code,
            show_toast: false,
          });
        }}
      />

      <WorkspaceConfigDrawer isOpen={showWorkspaceConfig} onClose={() => setShowWorkspaceConfig(false)} colors={colors} config={workspaceConfigState.config} onToggle={workspaceConfigState.toggle} transferRoulettes={transferRoulettes} />

      <ContactFeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} contactPhone={contactRef} contactName={displayName} colors={colors} displayMode="drawer" />

      <SavedMessagesDrawer
        isOpen={showSaved}
        onClose={() => setShowSaved(false)}
        colors={colors}
        channel={code}
        contactPhone={contactRef}
        contactName={displayName}
        onUseMessage={(m) => {
          const text = typeof m === 'string' ? m : (m?.content || m?.text || '');
          if (text) setInput(renderVariablesInText(text, { contactName: displayName, operatorName: user?.name }));
          if (m && typeof m === 'object') {
            const incoming = [];
            (m.attachments || []).forEach((a) => {
              if (!a?.file_url) return;
              incoming.push({ url: a.file_url, filename: a.file_name || 'arquivo', type: a.type || 'file', mimeType: a.mime_type, size: a.file_size });
            });
            if (m.audio?.file_url) {
              incoming.push({ url: m.audio.file_url, filename: m.audio.file_name || 'audio.webm', type: 'audio', mimeType: m.audio.mime_type || 'audio/webm' });
            }
            if (incoming.length) queueFiles(incoming);
          }
          setShowSaved(false);
        }}
      />

      <CreateAgentModal />
    </div>
  );
};

export default ChatWindow;
